import { SettlementType } from '@/connect';
import { UUID_EMPTY } from '@/constants';
import { AgreementInfo, RawAgreementInfo } from '@/modules/agreements/models';
import { Raw } from '@/modules/common/helpers/api';
import { CompanyInfo, RawCompanyInfo } from '@/modules/common/models';
import { RoundingRule } from '@/modules/sec-lending/helpers/contract-details';
import Decimal from 'decimal.js';

export type DeliverOrderSender = 'provable' | 'loanet' | 'self';

export type RawCompany = Raw<
  Company,
  {
    // always specify existing raw entry types explititly
    nsccAccounts: null | RawNsccAccount[];
    defaultOmsAgreements: null | RawAgreementInfo[];
    smartloanAgreements: null | RawAgreementInfo[];
    bilateralCounterparties: null | RawCompanyInfo[];
    sponsor: RawCompanyInfo | null;
    enabledSettlementTypes: Array<keyof typeof SettlementType>;
    complianceEmailAddresses: null | string[];
  }
>;

export class Company {
  public id: string;
  public name: string;
  public kind: string;
  public displayId: string;
  public tradingPermissions: number; // bit-set with allowed trade actions
  public defaultOrderSoftLimit: Decimal | null;
  public defaultOrderHardLimit: Decimal | null;
  public aggregateExposureLimit: Decimal | null;
  public executionsValueLimit: Decimal | null;
  public profileID: string;
  public profileLabel: string;
  public preferredIndependentAmountRate: Decimal;
  public preferredRoundingRule: RoundingRule | null;
  public nsccAccounts: NsccAccount[];
  public hasLoanetIntegration: boolean;
  public ipRestrictionsEnabled: boolean;
  public deliverOrderSender: DeliverOrderSender;
  public benchmarkType: string | null;
  public benchmarkPrice: Decimal | null;
  public defaultOmsAgreements: AgreementInfo[];
  public smartloanAgreements: AgreementInfo[];
  public smartloanEnabled: boolean;
  public bilateralCounterparties: CompanyInfo[];
  public sponsor: CompanyInfo | null;
  public enabledSettlementTypes: SettlementType[];
  public cutOffTime: string | null;
  public complianceEmailAddresses: string[];
  public smartLoanIntegrationUrl: string | null;
  public smartLoanIntegrationUsername: string | null;
  public dtcParticipantId: string;
  public autoDisableInactiveDays: number | null;

  protected constructor(data: RawCompany) {
    this.id = data.id;
    this.name = data.name;
    this.kind = data.kind;
    this.displayId = data.displayId;
    this.tradingPermissions = data.tradingPermissions;
    this.defaultOrderSoftLimit =
      data.defaultOrderSoftLimit === null ? null : new Decimal(data.defaultOrderSoftLimit);
    this.defaultOrderHardLimit =
      data.defaultOrderHardLimit === null ? null : new Decimal(data.defaultOrderHardLimit);
    this.aggregateExposureLimit =
      data.aggregateExposureLimit === null ? null : new Decimal(data.aggregateExposureLimit);
    this.executionsValueLimit =
      data.executionsValueLimit === null ? null : new Decimal(data.executionsValueLimit);
    this.profileID = data.profileID;
    this.profileLabel = data.profileLabel;
    this.preferredIndependentAmountRate = new Decimal(data.preferredIndependentAmountRate);
    this.preferredRoundingRule = data.preferredRoundingRule;
    this.nsccAccounts = (data.nsccAccounts ?? []).map(NsccAccount.fromData);
    this.hasLoanetIntegration = data.hasLoanetIntegration;
    this.ipRestrictionsEnabled = data.ipRestrictionsEnabled;
    this.deliverOrderSender = data.deliverOrderSender;
    this.benchmarkType = data.benchmarkType;
    this.benchmarkPrice = data.benchmarkPrice === null ? null : new Decimal(data.benchmarkPrice);
    this.defaultOmsAgreements = (data.defaultOmsAgreements ?? []).map(AgreementInfo.fromData);
    this.smartloanAgreements = (data.smartloanAgreements ?? []).map(AgreementInfo.fromData);
    this.smartloanEnabled = data.smartloanEnabled;
    this.bilateralCounterparties = (data.bilateralCounterparties ?? []).map<CompanyInfo>(
      CompanyInfo.fromData
    );
    this.sponsor = CompanyInfo.fromData(data.sponsor);
    this.enabledSettlementTypes = data.enabledSettlementTypes.map((est) => SettlementType[est]);
    this.cutOffTime = data.cutOffTime;
    this.complianceEmailAddresses = data.complianceEmailAddresses ?? [];
    this.smartLoanIntegrationUrl = data.smartLoanIntegrationUrl;
    this.smartLoanIntegrationUsername = data.smartLoanIntegrationUsername;
    this.dtcParticipantId = data.dtcParticipantId;
    this.autoDisableInactiveDays = data.autoDisableInactiveDays;
  }

  public static create(): Company {
    return Company.fromData(Company.createData());
  }

  public static createData(): RawCompany {
    return {
      id: UUID_EMPTY,
      name: '',
      kind: 'client',
      displayId: '',
      tradingPermissions: 0,
      defaultOrderSoftLimit: null,
      defaultOrderHardLimit: null,
      aggregateExposureLimit: null,
      executionsValueLimit: null,
      profileID: '',
      profileLabel: '',
      preferredIndependentAmountRate: '0',
      preferredRoundingRule: null,
      nsccAccounts: [],
      hasLoanetIntegration: false,
      ipRestrictionsEnabled: false,
      deliverOrderSender: 'self',
      benchmarkType: null,
      benchmarkPrice: null,
      defaultOmsAgreements: [],
      smartloanAgreements: [],
      smartloanEnabled: false,
      bilateralCounterparties: [],
      sponsor: null,
      enabledSettlementTypes: [],
      cutOffTime: null,
      complianceEmailAddresses: [],
      smartLoanIntegrationUrl: null,
      smartLoanIntegrationUsername: null,
      dtcParticipantId: '',
      autoDisableInactiveDays: null,
    };
  }

  public static clone(model: Company): Company {
    return Company.fromData(Company.toData(model));
  }

  public static fromData(data: RawCompany): Company {
    return new Company(data);
  }

  public static toData(model: Company): RawCompany {
    return {
      id: model.id,
      name: model.name,
      kind: model.kind,
      displayId: model.displayId,
      tradingPermissions: model.tradingPermissions,
      defaultOrderSoftLimit:
        model.defaultOrderSoftLimit === null ? null : model.defaultOrderSoftLimit.toString(),
      defaultOrderHardLimit:
        model.defaultOrderHardLimit === null ? null : model.defaultOrderHardLimit.toString(),
      aggregateExposureLimit:
        model.aggregateExposureLimit === null ? null : model.aggregateExposureLimit.toString(),
      executionsValueLimit:
        model.executionsValueLimit === null ? null : model.executionsValueLimit.toString(),
      profileID: model.profileID,
      profileLabel: model.profileLabel,
      preferredIndependentAmountRate: model.preferredIndependentAmountRate.toString(),
      preferredRoundingRule: model.preferredRoundingRule,
      nsccAccounts: model.nsccAccounts.map(NsccAccount.toData),
      hasLoanetIntegration: model.hasLoanetIntegration,
      ipRestrictionsEnabled: model.ipRestrictionsEnabled,
      deliverOrderSender: model.deliverOrderSender,
      benchmarkType: model.benchmarkType,
      benchmarkPrice: model.benchmarkPrice === null ? null : model.benchmarkPrice.toString(),
      defaultOmsAgreements: model.defaultOmsAgreements.map(AgreementInfo.toData),
      smartloanAgreements: model.smartloanAgreements.map(AgreementInfo.toData),
      smartloanEnabled: model.smartloanEnabled,
      bilateralCounterparties: model.bilateralCounterparties.map(CompanyInfo.toData),
      sponsor: model.sponsor === null ? null : CompanyInfo.toData(model.sponsor),
      enabledSettlementTypes: model.enabledSettlementTypes.map(
        (est) => SettlementType[est] as keyof typeof SettlementType
      ),
      cutOffTime: model.cutOffTime,
      complianceEmailAddresses: model.complianceEmailAddresses
        .map((address) => address.trim())
        .filter(Boolean),
      smartLoanIntegrationUrl: model.smartLoanIntegrationUrl,
      smartLoanIntegrationUsername: model.smartLoanIntegrationUsername,
      dtcParticipantId: model.dtcParticipantId,
      autoDisableInactiveDays: model.autoDisableInactiveDays,
    };
  }
}

export type RawCompanyRequest = Raw<
  CompanyRequest,
  {
    nsccAccounts: null | RawNsccAccount[];
    bilateralCounterpartyIds: null | string[];
    sponsorCompanyId: string | null;
    enabledSettlementTypes: null | Array<keyof typeof SettlementType>;
    smartLoanIntegrationUrl?: string | null;
    smartLoanIntegrationUsername?: string | null;
    smartLoanIntegrationPassword?: string | null;
    complianceEmailAddresses: null | string[];
    defaultOrderSoftLimit: string | null;
    defaultOrderHardLimit: string | null;
  },
  'sponsorCompany' | 'bilateralCounterparties' | 'defaultOmsAgreements' | 'smartloanAgreements'
>;

export class CompanyRequest {
  public name: string;
  public kind: string;
  public displayId: string;
  public tradingPermissions: number; // bit-set with allowed trade actions
  public profileID: string;
  public nsccAccounts: NsccAccount[];
  public bilateralCounterparties: CompanyInfo[];
  public hasLoanetIntegration: boolean;
  public deliverOrderSender: string;
  public sponsorCompany: CompanyInfo | null;
  public enabledSettlementTypes: string[];
  public complianceEmailAddresses: string[];
  public dtcParticipantId: string;
  public smartLoanIntegrationUrl: string | null;
  public smartLoanIntegrationUsername: string | null;
  public smartLoanIntegrationPassword: string | null;
  public defaultOmsAgreements: AgreementInfo[];
  public smartloanAgreements: AgreementInfo[];
  public smartloanEnabled: boolean;
  public autoDisableInactiveDays: number | null;
  public defaultOrderSoftLimit: Decimal | null;
  public defaultOrderHardLimit: Decimal | null;

  protected constructor(company: Company) {
    this.name = company.name;
    this.kind = company.kind;
    this.displayId = company.displayId;
    this.tradingPermissions = company.tradingPermissions;
    this.profileID = company.profileID;
    this.nsccAccounts = (company.nsccAccounts ?? []).map(NsccAccount.fromData);
    this.bilateralCounterparties = company.bilateralCounterparties ?? [];
    this.hasLoanetIntegration = company.hasLoanetIntegration;
    this.deliverOrderSender = company.deliverOrderSender;
    this.sponsorCompany = company.sponsor;
    this.enabledSettlementTypes = company.enabledSettlementTypes.map(
      (settlementType) => SettlementType[settlementType]
    );
    this.complianceEmailAddresses = company.complianceEmailAddresses ?? [];
    this.dtcParticipantId = company.dtcParticipantId;
    this.smartLoanIntegrationUrl = company.smartLoanIntegrationUrl ?? null;
    this.smartLoanIntegrationUsername = company.smartLoanIntegrationUsername ?? null;
    this.smartLoanIntegrationPassword = null;
    this.defaultOmsAgreements = company.defaultOmsAgreements;
    this.smartloanAgreements = company.smartloanAgreements;
    this.smartloanEnabled = company.smartloanEnabled;
    this.autoDisableInactiveDays = company.autoDisableInactiveDays;
    this.defaultOrderSoftLimit = company.defaultOrderSoftLimit;
    this.defaultOrderHardLimit = company.defaultOrderHardLimit;
  }

  public static create(): CompanyRequest {
    return CompanyRequest.fromCompany(Company.create());
  }

  public static fromCompany(company: Company): CompanyRequest {
    return new CompanyRequest(company);
  }

  public static toData(model: CompanyRequest): RawCompanyRequest {
    return {
      name: model.name,
      kind: model.kind,
      displayId: model.displayId,
      tradingPermissions: model.tradingPermissions,
      profileID: model.profileID,
      nsccAccounts: model.nsccAccounts.map(NsccAccount.toData),
      bilateralCounterpartyIds: model.bilateralCounterparties.map(({ companyId }) => companyId),
      hasLoanetIntegration: model.hasLoanetIntegration,
      deliverOrderSender: model.deliverOrderSender,
      sponsorCompanyId: model.sponsorCompany?.companyId ?? null,
      enabledSettlementTypes: model.enabledSettlementTypes.map(
        (est) => est as keyof typeof SettlementType
      ),
      complianceEmailAddresses: model.complianceEmailAddresses,
      dtcParticipantId: model.dtcParticipantId,
      smartLoanIntegrationUrl: model.smartLoanIntegrationUrl,
      smartLoanIntegrationUsername: model.smartLoanIntegrationUsername,
      smartLoanIntegrationPassword: model.smartLoanIntegrationPassword,
      autoDisableInactiveDays: model.autoDisableInactiveDays,
      smartloanEnabled: model.smartloanEnabled,
      defaultOrderSoftLimit:
        model.defaultOrderSoftLimit === null ? null : model.defaultOrderSoftLimit.toString(),
      defaultOrderHardLimit:
        model.defaultOrderHardLimit === null ? null : model.defaultOrderHardLimit.toString(),
    };
  }
}

export type RawCompanyResponse = Raw<
  CompanyResponse,
  {
    company: RawCompany;
  }
>;

export class CompanyResponse {
  public status: string;
  public company: Company;

  protected constructor(data: RawCompanyResponse) {
    this.status = data.status;
    this.company = Company.fromData(data.company);
  }

  public static fromData(data: RawCompanyResponse): CompanyResponse {
    return new CompanyResponse(data);
  }

  public static toData(model: CompanyResponse): RawCompanyResponse {
    return {
      status: model.status,
      company: Company.toData(model.company),
    };
  }
}

export type RawCompanyListResponse = Raw<
  CompanyListResponse,
  {
    // always specify existing raw entry types explititly
    companies: RawCompany[];
  }
>;

export class CompanyListResponse {
  public status: string;
  public companies: Company[];

  protected constructor(data: RawCompanyListResponse) {
    this.status = data.status;
    this.companies = data.companies.map(Company.fromData);
  }

  public static fromData(data: RawCompanyListResponse): CompanyListResponse {
    return new CompanyListResponse(data);
  }

  public static toData(model: CompanyListResponse): RawCompanyListResponse {
    return {
      status: model.status,
      companies: model.companies.map(Company.toData),
    };
  }
}

export type RawNsccAccount = Omit<Raw<NsccAccount>, 'key' | 'errorMainID' | 'errorSubID'>;

export class NsccAccount {
  public mainID: string;
  public subID: string;
  public isActive: boolean;

  // form helpers
  public key: number;
  // validation
  public errorMainID: string;
  public errorSubID: string;

  protected constructor(data: RawNsccAccount) {
    this.mainID = data.mainID;
    this.subID = data.subID;
    this.isActive = data.isActive;

    this.key = 0;
    this.errorMainID = '';
    this.errorSubID = '';
  }

  public static fromData(data: RawNsccAccount): NsccAccount {
    return new NsccAccount(data);
  }

  public static toData(model: NsccAccount): RawNsccAccount {
    return {
      mainID: model.mainID.toUpperCase(),
      subID: model.subID.toUpperCase(),
      isActive: model.isActive,
    };
  }
}
